import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import {
  usePatientInsurancesQuery,
  useCreatePatientInsuranceMutation,
  useUpdatePatientInsuranceMutation,
  useDeletePatientInsuranceMutation,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting';
import { paths } from '@common/types';
import {
  medicareInsuranceEntrySchema,
  MedicareEntryFormShape,
} from '../schemas';

const formSchema = yup.object({ entry: medicareInsuranceEntrySchema });

type CreateInsuranceBody =
  paths['/patient/{patient_id}/insurances']['post']['requestBody']['content']['application/json'];

type UpdateInsuranceBody =
  paths['/patient/{patient_id}/insurances/{insurance_id}']['patch']['requestBody']['content']['application/json'];

const toCreateBody = (entry: MedicareEntryFormShape): CreateInsuranceBody => ({
  type: 'medicare',
  insurance_company: entry.payer_name.id,
  insurance_company_name: entry.payer_name.name,
  insurance_id: entry.mbi ?? '',
  mbi: entry.mbi,
  payer_type: entry.payer_type as never,
  payment_type: entry.payment_type as never,
});

const toUpdateBody = (entry: MedicareEntryFormShape): UpdateInsuranceBody => ({
  insurance_company: entry.payer_name.id,
  insurance_company_name: entry.payer_name.name,
  insurance_id: entry.mbi ?? '',
  mbi: entry.mbi,
  payer_type: entry.payer_type as never,
  payment_type: entry.payment_type as never,
});

export type MedicareFormShape = {
  entry: MedicareEntryFormShape;
};

export const defaultMedicareEntry: MedicareEntryFormShape = {
  id: undefined,
  payer_name: { id: '', name: '' },
  payer_type: '',
  mbi: '',
  payment_type: '',
};

export const useMedicareForm = (patient_id: string) => {
  const { data: patientInsurances } = usePatientInsurancesQuery({ patient_id });

  const { mutateAsync: createInsurance } = useCreatePatientInsuranceMutation({
    patient_id,
  });
  const { mutateAsync: updateInsurance } = useUpdatePatientInsuranceMutation({
    patient_id,
  });
  const { mutateAsync: deleteInsurance } = useDeletePatientInsuranceMutation({
    patient_id,
  });
  const formMethods = useForm<MedicareFormShape>({
    mode: 'all',
    resolver: yupResolver(formSchema as never),
    defaultValues: { entry: defaultMedicareEntry },
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid },
  } = formMethods;

  const buildValues = useCallback((): MedicareFormShape => {
    const existing = patientInsurances?.primary_medicare[0];
    const mbi = patientInsurances?.mbi ?? '';
    if (!existing)
      return {
        entry: { ...defaultMedicareEntry, mbi },
      };
    return {
      entry: {
        id: existing.id,
        payer_name: existing.payer_name
          ? { id: existing.insurance_company ?? '', name: existing.payer_name }
          : { id: '', name: '' },
        payer_type: existing.payer_type ?? '',
        mbi,
        payment_type: existing.payment_type ?? '',
      },
    };
  }, [patientInsurances]);

  const resetForm = useCallback(() => {
    reset(buildValues());
  }, [reset, buildValues]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const clearEntry = useCallback(async () => {
    const currentId = formMethods.getValues('entry.id');
    if (!currentId) {
      resetForm();
      return;
    }
    try {
      await deleteInsurance({ insurance_id: currentId });
      toast.success('Medicare insurance deleted successfully');
    } catch (error) {
      toast.error(`Delete failed: ${formatApiError(error)}`);
    }
  }, [formMethods, resetForm, deleteInsurance]);

  const submitHandler = useCallback(
    async (values: MedicareFormShape) => {
      const entry = values.entry;
      if (!isDirty) return true;
      try {
        if (entry.id) {
          await updateInsurance({ id: entry.id, ...toUpdateBody(entry) });
          toast.success('Update Medicare insurance successful');
        } else {
          await createInsurance(toCreateBody(entry));
          toast.success('Create Medicare insurance successful');
        }
        return true;
      } catch (error) {
        toast.error(`Medicare insurance failed: ${formatApiError(error)}`);
        return false;
      }
    },
    [isDirty, createInsurance, updateInsurance],
  );

  const submit = useCallback(
    () =>
      new Promise<boolean>(resolve => {
        handleSubmit(
          async values => {
            resolve(await submitHandler(values));
          },
          () => {
            resolve(false);
          },
        )();
      }),
    [handleSubmit, submitHandler],
  );

  return {
    formMethods,
    isDirty,
    isValid,
    isSubmitting,
    resetForm,
    clearEntry,
    submit,
  };
};
